import React from 'react'

export interface Editorial {
  slug: string
  title: string
  label: string
  category: string
  publishedAt: string
  summary: string
  welcomeGift: boolean
  subtitle?: string
  content: React.ReactNode
}

export const editorials: Editorial[] = [
  {
    slug: 'is-custom-made-fashion-right-for-you',
    title: 'Is Custom-Made Fashion Right for You?',
    label: 'Begin Here',
    category: 'Begin Here',
    publishedAt: '2026-07-20',
    summary:
      'Before requesting your first custom-made piece, discover whether this ' +
      'way of buying clothing aligns with your expectations, lifestyle and values.',
    welcomeGift: true,
    content: (
      <>
        <p>
          We live in a world where clothing is designed to fit millions of
          people but rarely feels like it was made for one. Every body is
          grouped into a size category, while many of the factors each person
          requires to achieve the best possible fit are ignored. Fit is not only
          about how clothing looks. It is also about how the wearer feels in it.
        </p>

        <p>
          Custom-made fashion created from scratch offers a different
          experience. The aim is not to own more clothing. Instead, it focuses
          on owning fewer pieces that fit beautifully, reflect your
          individuality and are created with intention.
        </p>

        <p>But custom-made fashion is not for everyone.</p>

        <p>
          Before requesting your first custom-made piece, it is worth
          understanding whether this way of buying clothing aligns with your
          expectations, lifestyle and values.
        </p>

        <h2>Who enjoys custom-made fashion most?</h2>

        <p>
          For decades, commissioning a custom order from a tailor was one of the
          primary ways people acquired clothing. Today, this approach is often
          chosen by people who value the following.
        </p>

        <h3>Individual style</h3>

        <p>
          Not everyone wants to look like everybody else. In a templated world,
          where standards about how people should look and what they should wear
          dominate many retail stores, it is not surprising that more people are
          craving a unique and personal style.
        </p>

        <p>
          There is a sense of identity that comes with developing your own
          individual style. Custom-made fashion gives you the opportunity to
          participate in creating clothing that expresses who you are rather
          than simply selecting from what is already available.
        </p>

        <h3>Quality over quantity</h3>

        <p>
          As familiar as the phrase quality over quantity may sound, low-quality
          clothing remains common throughout the mass-production fashion
          industry.
        </p>

        <p>
          People who prioritise high quality often decide to move in the
          opposite direction. They are not looking for a large quantity of
          clothing at the lowest possible price. Instead, they place greater
          importance on owning a well-made piece, even when it requires paying
          more.
        </p>

        <p>This is where the idea that less is more truly holds meaning.</p>

        <h3>Longevity</h3>

        <p>
          Good quality and longevity work hand in hand. People who value
          custom-made fashion are often looking for pieces that perform well and
          continue to do so for a long period of time.
        </p>

        <p>
          Durability matters. A thoughtfully made piece has the potential to
          remain part of your wardrobe far longer than clothing designed
          primarily for speed, volume and short-term trends.
        </p>

        <h3>Fit</h3>

        <p>
          What is the purpose of bespoke or custom clothing if the process still
          relies entirely on general standard measurements?
        </p>

        <p>
          People choose custom-made fashion because it considers their
          individual fit and preferences throughout the stages of creating the
          piece. Measurements, proportions, comfort and personal requirements
          can all influence the final result.
        </p>

        <p>
          No one truly wants an item that feels as though it was made for
          everyone else. When you choose a piece of clothing, you often
          visualise it on yourself. You picture how you will wear it and imagine
          how it will fit into your own life.
        </p>

        <p>
          There is a deeper sense of fulfilment in having something crafted for
          you, with you in mind from the beginning. People who value fit
          understand this, and it is one of the reasons they enjoy custom-made
          clothing.
        </p>

        <h3>Craftsmanship</h3>

        <p>
          Even as fashion becomes increasingly digital, the human element can
          never be completely removed from clothing. Human beings wear clothes,
          and clothes are made by human hands.
        </p>

        <p>
          Every piece of clothing, whether inexpensive or luxurious, is sewn by
          someone guiding fabric through a sewing machine. However,
          mass-produced clothing often allows limited time for detailed
          construction, individual fit and careful finishing.
        </p>

        <p>
          This is one of the qualities that makes custom-made apparel different.
          It can be cut according to your measurements, created around your
          requirements and constructed specifically for you with close attention
          to detail.
        </p>

        <p>
          People who enjoy custom-made fashion value the craftsmanship involved
          in creating a one-of-one piece.
        </p>

        <h3>Supporting independent makers</h3>

        <p>
          Custom-made fashion creates a direct relationship between the person
          wearing the piece and the person making it. They love shopping from
          small business owners.
        </p>

        <p>
          Custom-made designers, craftsmen and artisans are the hardest to find
          these days as the craft continues to dwindle lucratively. Those who
          enjoy custom-made fashion value the craft and often want to support
          the people who make them. Most Custom-made fashion shoppers are hungry
          for accountability, relationships and real interactions that is
          lacking in mass-produced and fast fashion market. Rather than
          purchasing an anonymous finished product, they prefer to collaborate
          with an independent maker who can help bring their desired piece to
          life.
        </p>

        <h2>It may not be the right choice if...</h2>

        <p>Custom-made fashion may not be ideal for you if you:</p>

        <ul>
          <li>Need something immediately.</li>
          <li>
            Prefer to change your wardrobe frequently with each new trend.
          </li>
          <li>
            Do not enjoy collaborating during the design or making process.
          </li>
          <li>Place current trends above individual fit.</li>
          <li>Prefer quantity over the quality of the finished product.</li>
        </ul>

        <h2>It may be perfect for you if...</h2>

        <p>Custom-made fashion may suit you if you:</p>

        <ul>
          <li>Appreciate craftsmanship.</li>
          <li>
            Enjoy owning a one-of-one piece personalised specifically for you.
          </li>
          <li>Value long-term quality.</li>
          <li>Want clothing that reflects who you are.</li>
          <li>
            Value the accountability and transparency that can come from working
            directly with a custom maker who creates things from scratch.
          </li>
        </ul>

        <h2>Questions to ask yourself</h2>

        <ul>
          <li>Do I value individuality over convenience?</li>
          <li>Am I willing to wait for something exceptional?</li>
          <li>Would I rather own fewer, better pieces?</li>
          <li>Do I enjoy understanding the story behind what I wear?</li>
        </ul>

        <h2>Final thoughts</h2>

        <p>
          The greatest value of custom-made fashion is not simply owning better
          clothing. It&apos;s participating in the creation of something made
          specifically for you. When you value craftsmanship, and intentional
          design, custom-made fashion becomes more than a purchase. It becomes a
          lasting investment in how you choose to dress and express yourself.
        </p>
      </>
    )
  },

  {
    slug: 'questions-to-ask-before-placing-a-custom-made-order',
    title: 'Questions to Ask Before Placing a Custom-Made Order',
    label: 'Begin Here',
    category: 'Begin Here',
    publishedAt: '2026-07-22',
    summary:
      'Knowing what to ask before placing a custom-made order helps you choose ' +
      'the right maker, understand the process and avoid unnecessary surprises ' +
      'along the way.',
    welcomeGift: true,
    content: (
      <>
        <p>
          Most people approach ordering a custom-made outfit with an unspoken
          concern.
        </p>

        <p>People fear:</p>

        <ul>
          <li>&ldquo;Will it look like I imagine?&rdquo;</li>
          <li>&ldquo;Will the custom designer understand me?&rdquo;</li>
          <li>&ldquo;Is it worth the wait?&rdquo;</li>
        </ul>

        <p>
          There is nothing worse than waiting a long time for something that was
          created specifically for you, only for the entire process to fall very
          short of your expectations.
        </p>

        <p>
          When it comes to placing a custom order, clear communication is
          everything.
        </p>

        <p>
          It is a beautiful and collaborative journey, so don&apos;t be afraid
          to express your thoughts, ask questions and seek clarification. This
          is what differentiates a custom-made piece from a ready-to-wear piece.
          Your preferences are what guide the production of the apparel.
        </p>

        <p>
          Here are the right questions to ask before ordering a custom-made
          outfit, giving you greater confidence throughout the process.
        </p>

        <h2>Costs and Payment</h2>

        <ul>
          <li>How much is the total cost?</li>
          <li>Are there additional costs that may be added along the way?</li>
          <li>How is payment made?</li>
          <li>Is it a down payment or full payment upfront?</li>
          <li>Is shipping included?</li>
        </ul>

        <p>
          The cost of producing a custom-made garment is one of the most
          controversial parts of custom-made fashion because there is no set
          pricing. It can easily go over budget, and costs can accumulate if
          they are not properly managed. Therefore, it is important to clarify
          what contributes to the total cost and what incurs additional charges.
        </p>

        <p>
          The total cost of production is calculated based on various factors
          such as the yardage of fabric, yardage of lining, interfacing,
          corsetry, the number of hours spent producing the garment and other
          supporting materials.
        </p>

        <p>
          There are usually additional costs that arise as the design evolves
          based on the client&apos;s requirements, so keep this in mind to avoid
          any surprises.
        </p>

        <p>
          The type of fabric, the number of fitting sessions, zippers, buttons,
          lining, sourcing materials and labour all contribute to the total
          cost. However, if changes are made to the initially agreed-upon
          design, additional charges are usually added.
        </p>

        <p>
          It&apos;s also important to know how payment is made because it helps
          you understand whether it is a secure mode of payment. Asking about
          deposits versus full payment upfront also gives you a better
          understanding of how the custom maker works and what you should
          expect.
        </p>

        <h2>Materials and Design</h2>

        <ul>
          <li>
            Who purchases the fabric and all the materials needed such as
            zippers, buttons and lining?
          </li>
          <li>Can I see examples of your previous work?</li>
          <li>What range and quality of fabrics do you work with?</li>
        </ul>

        <p>
          Who purchases the fabric and materials generally falls into three
          approaches depending on the work style of the custom maker and what
          you, as the client, are looking for.
        </p>

        <h3>Option 1</h3>

        <p>
          The maker begins with a sketch based on your design, creates a muslin
          mock-up and then produces the final garment.
        </p>

        <p>
          Usually there is a base price, but it increases depending on the
          complexity of the design and the type of fabric you choose. The custom
          maker purchases the fabric through their suppliers and includes the
          fabric, supporting materials and labour in the total price.
        </p>

        <h3>Option 2</h3>

        <p>
          Some custom makers require you to purchase your own fabric before
          production begins. They will tell you how many yards of fabric are
          needed to achieve your desired design.
        </p>

        <p>
          For the most part, they do not source fabric for you because they do
          not want to be responsible if you later dislike the fabric or colour
          that was selected.
        </p>

        <h3>Option 3</h3>

        <p>This third approach is becoming increasingly popular.</p>

        <p>
          The custom maker displays their portfolio and catalogue either
          in-store or online, allowing you to choose a design before requesting
          customisations.
        </p>

        <p>
          This usually starts with a base price that includes the fabric,
          materials, labour and fittings.
        </p>

        <p>
          It is considered the best of both worlds because you already know the
          maker&apos;s work before placing an order. You can then customise the
          design, fabric or colour. Any changes beyond the original design are
          usually added to the base price.
        </p>

        <h2>Timeline and Fittings</h2>

        <ul>
          <li>What is the production timeline and final delivery date?</li>
          <li>How are measurements taken?</li>
          <li>Will I receive updates throughout production?</li>
          <li>Will I be notified when my outfit is finished and shipped?</li>
          <li>How many fitting sessions are included?</li>
          <li>Do I need to travel for every fitting?</li>
        </ul>

        <p>
          Timelines are another important aspect of custom-made fashion. In
          fact, they are one of the main reasons some people avoid ordering
          custom-made clothing.
        </p>

        <p>
          Most people commission custom-made outfits for weddings, milestone
          birthdays, graduations, proms and other important occasions where
          deadlines matter.
        </p>

        <p>
          Asking how long production will take helps you determine whether a
          particular maker is the right fit for your timeline.
        </p>

        <p>
          While most custom makers can provide an estimated completion or
          delivery date, it is equally important to ask whether they provide
          updates throughout production.
        </p>

        <p>
          Being carried along during the process helps you understand what stage
          your garment is in and provides greater confidence throughout the
          journey.
        </p>

        <p>
          Fittings also contribute significantly to the production timeline.
          They help both you and the maker determine whether the garment fits as
          intended.
        </p>

        <p>
          Measurements are the first stage of the fitting process and are
          arguably the most important part of custom-made fashion.
        </p>

        <p>
          Different makers obtain measurements in different ways. Some measure
          clients in person, while others measure remotely using digital tools.
          Accurate body measurements become the foundation on which your garment
          is built.
        </p>

        <p>
          If the tailor is located in a different city or country, they may
          travel to you or require you to travel for fittings. Travel costs are
          usually added to the total cost and may also increase the production
          timeline.
        </p>

        <p>
          Today, many custom makers are exploring online measurement solutions
          such as the Virtual Tailor Measuring Tool (OVTMT) instead of requiring
          clients to visit a studio. This helps reduce travel, lowers costs and
          makes custom-made fashion more accessible.
        </p>

        <p>
          When it comes to fittings, some tailors are able to achieve an
          excellent fit after the first fitting, while others may require three
          or four sessions. It ultimately depends on the skill of the custom
          maker.
        </p>

        <p>
          One thing worth considering is that when the same custom maker takes
          the measurements, drafts the pattern, cuts the fabric and constructs
          the garment, they are often able to achieve a better fit more
          efficiently than when several people are involved throughout the
          process.
        </p>

        <p>
          In other words, working directly with the person making your garment
          often leads to a more consistent result.
        </p>

        <h2>Final Thoughts</h2>

        <p>
          Custom-made fashion is not meant to be a tedious process filled with
          uncertainty. Ordering a custom-made item should feel exciting,
          personal and intentional.
        </p>

        <p>
          Asking the right questions helps you understand the maker, the process
          and what to expect.
        </p>

        <p>
          The better the communication from the beginning, the better the
          outcome is likely to be.
        </p>
      </>
    )
  },

  {
    slug: 'how-the-custom-made-process-works',
    title: 'How the Custom-Made Process Works',
    label: 'Begin Here',
    category: 'Begin Here',
    publishedAt: '2026-07-26',
    summary:
      'From the initial consultation and design decisions to measurements, ' +
      'craftsmanship, fittings and delivery, understand what to expect ' +
      'throughout the custom-making process.',
    welcomeGift: true,
    content: (
      <>
        <p>
          You found a maker whose work you admire, and you&apos;ve decided to
          order a custom-made outfit. Now, what next?
        </p>

        <p>
          Custom-made fashion requires you, as the buyer, to collaborate
          directly with the maker, whether you shop online or in person, unlike
          its mass-produced counterpart.
        </p>

        <p>
          With mass-produced fashion, ready-to-wear and off-the-rack fashion,
          all you need to do is take one step: add to cart. The finished
          products are already on hand. This has been part of its appeal since
          its emergence in the fashion industry.
        </p>

        <p>
          However, It has created a gap in people&apos;s understanding of what
          the custom-making process is and how it works.
        </p>

        <p>
          In this article, you&apos;ll gain more understanding of how the
          custom-made process works, from the initial consultation to the final
          fitting and delivery. This, in turn, makes the process less cumbersome
          and more satisfying.
        </p>

        <h2>The Consultation</h2>

        <p>
          Every custom piece starts with a conversation. This is when you and
          the custom maker discuss what you want made.
        </p>

        <p>Topics often include:</p>

        <ul>
          <li>What you&apos;re looking for</li>
          <li>When you need it</li>
          <li>Costs</li>
          <li>Fabrics and other materials</li>
          <li>Inspiration, depending on what you&apos;re looking for</li>
          <li>Lifestyle, which some custom makers may include</li>
        </ul>

        <h2>Design Decisions</h2>

        <p>
          After the custom maker understands what you are looking for, the next
          step is discussing the details that bring everything together to make
          it a custom piece created uniquely for you.
        </p>

        <p>Examples include:</p>

        <ul>
          <li>Silhouette</li>
          <li>Collar or neckline</li>
          <li>Sleeve style</li>
          <li>Buttons</li>
          <li>Corset, if applicable</li>
          <li>Lining</li>
          <li>Pockets</li>
          <li>Finishes</li>
        </ul>

        <p>This is usually part of the consultation.</p>

        <p>
          Each decision contributes to the drafting of the pattern that reflects
          your personal style.
        </p>

        <h2>Measurements</h2>

        <p>
          The next step that naturally follows is obtaining your measurements.
        </p>

        <p>
          Accurate measurements are the foundation of custom-made clothing. This
          stage ensures that the garment is made specifically for your body.
        </p>

        <p>
          Depending on the maker, measurements may be taken in several ways.
        </p>

        <h3>In Person</h3>

        <p>
          This is often the best way to achieve accurate measurements because
          the custom maker can see you and measure you using a measuring tape.
        </p>

        <p>
          Taking measurements has many nuances that are subjective to each
          custom maker and how they work to achieve the best fit. Seeing you and
          taking the measurements themselves allows them to achieve this more
          effortlessly.
        </p>

        <h3>Virtual Measurements</h3>

        <p>
          Virtual measurements are also known as online measurements. This
          approach involves using tools or applications to take a client&apos;s
          measurements.
        </p>

        <p>
          There are various measurement applications available but most focus
          mainly on generating precalculated measurements rather than preserving
          the direct involvement of the Custom maker.
        </p>

        <p>
          This is why we created the Virtual Tailor Measuring Tool, also known
          as OVTMT. It allows the custom maker to still see you and measure you
          using your 3D model.
        </p>

        <p>
          Custom makers often use this approach for online clients or as an
          alternative when a client is not available for an in-person visit.
        </p>

        <h3>Using Detailed Measurement Guides</h3>

        <p>
          This involves using picture illustrations or written instructions to
          help the client measure themselves or have a family member measure
          them.
        </p>

        <p>
          Some custom makers also include video calls with the client to provide
          instructions on how the measurements should be taken.
        </p>

        <h2>Craftsmanship</h2>

        <p>
          After everything is agreed upon, the maker begins to create. This is
          where the construction of the garment takes place, from pattern
          drafting to cutting and then stitching.
        </p>

        <p>
          Unlike mass production, each piece is crafted with individual
          attention, with you in mind. Your specifications are not only
          considered; they are required to be included.
        </p>

        <h2>Fitting and Delivery</h2>

        <p>
          Fittings help you and the maker know whether what has been crafted for
          you fits well.
        </p>

        <p>
          Some custom tailors are able to achieve the best fit at the first
          instance, requiring little to no fittings, while other tailors may
          require three or four fitting sessions. It all depends on the skill of
          the custom tailor.
        </p>

        <p>
          If the desired fit has not yet been achieved, adjustments and
          refinements are made to arrive at the best fit and allow for comfort,
          movement, proportion and ease.
        </p>

        <p>
          Once the fit and finishing are approved, the garment is completed and
          prepared for collection or delivery.
        </p>
        <h2>Final Thoughts</h2>

        <p>
          For many people, the custom-making process is unfamiliar. Unlike
          buying ready-to-wear clothing, custom-made fashion is collaborative.
        </p>

        <p>
          You&apos;re not simply purchasing a finished product. You&apos;re
          working with a skilled maker to create something designed specifically
          for you.
        </p>

        <p>
          Understanding the process and knowing what to expect makes the
          experience smoother and more enjoyable.
        </p>
      </>
    )
  },
  {
    slug: 'how-to-choose-the-right-custom-maker',
    title: 'How to Choose the Right Custom Maker for You',
    label: 'Weekly Edition',
    category: 'Weekly Edition',
    publishedAt: '2026-08-02',
    summary:
      'Choosing a custom maker is personal. Learn what to consider when reviewing their specialty, quality of work, process and communication style.',
    welcomeGift: false,
    content: (
      <>
        <p>
          Creating custom work, whether suits, dresses, shoes, jewelry or bags,
          requires you to work closely with the maker.
        </p>

        <p>There is often an inner concern:</p>

        <ul>
          <li>Will the custom maker create what I&apos;m looking for?</li>
          <li>
            Will they listen to and understand the specifications I request?
          </li>
          <li>What happens if the result is the opposite of what I wanted?</li>
        </ul>

        <p>
          You may have heard the saying that the customer is always right, but in
          this case, where both the custom maker and customer are working
          together, this phrase is placed in the back seat. Both seek to work
          together.
        </p>

        <p>
          The custom maker has the responsibility of considering and listening
          to the customer&apos;s request while giving recommendations to help
          refine it and achieve the best result.
        </p>

        <p>
          The customer is also responsible for providing the necessary
          information that will help the custom maker achieve the best result
          possible. This is the reason it is a collaboration.
        </p>

        <p>
          Choosing a custom maker is very personal, and your choice should align
          with what you are looking for.
        </p>

        <p>
          Here are some of the things that can help you know if someone is the
          right custom maker for you.
        </p>

        <h2>The Maker&apos;s Area of Specialty</h2>

        <p>
          You should approach a custom maker based on what you want to have made
          and what they specialize in.
        </p>

        <p>
          Most custom makers choose an area of focus, so they may not always be
          good at everything. This is why it is important to view the custom
          maker&apos;s work. It helps you see if their style and experience align
          with what you are looking for.
        </p>

        <h2>The Finishing and Quality of Work</h2>

        <p>
          This is a determining factor for people who are particular about the
          finishing of a garment or custom piece.
        </p>

        <p>
          For example, some people want extra lining in the inner parts of a
          piece, while this may not matter as much to others. They may be
          satisfied as long as the piece is visually appealing and the aesthetics
          are attractive.
        </p>

        <p>
          Some people give extra attention to the stitching, while others do not.
          Depending on your preferences, you can choose a custom maker based on
          the details and quality of their finishing.
        </p>

        <h2>Experience, Reviews and Reliability</h2>

<p>
  A maker&apos;s portfolio shows what they can create, but it is also helpful
  to understand how they work with clients. Look for reviews, references or
  examples of completed orders similar to what you want.
</p>

<p>
  Pay attention to whether the maker communicates timelines clearly, explains
  pricing and follows through on agreed expectations. Good craftsmanship is
  important, but reliability is also part of a successful custom-making
  experience.
</p>

        <h2>What You Are Looking for Versus How the Maker Works</h2>

        <p>
          Custom makers often work in different ways. Understanding their process
          helps you know whether it matches the experience you are looking for.
        </p>

        <p>There are usually three common approaches.</p>

        <h3>1. The Maker Who Designs on Demand</h3>

        <p>
          This maker starts with a sketch based on what you want for your design.
          They may help refine your idea, source the materials and create the
          piece from the beginning.
        </p>

        <p>
          The custom maker purchases the fabric through their suppliers and
          charges you for the cost of the fabric, the other materials and the
          labor involved.
        </p>

        <h3>2. The Maker With a Portfolio or Catalogue</h3>

        <p>
          This maker displays their portfolio or catalogue either in a physical
          store or online. You then make your choice based on what you see.
        </p>

        <p>
          After you choose a design, the maker drafts and creates your piece based
          on your customized requests. These may include changes to the design,
          fabric, color or other details.
        </p>

        <h3>3. The Maker Who Works With Your Fabric and Design</h3>

        <p>
          In this case, you already have the fabric and the style you want to have
          sewn.
        </p>

        <p>
          You will look for custom makers who require you to already purchase your fabric
          and provide a picture, sample or clear description of what you want
          made.
        </p>

        <h2>Signs the Maker May Not Be the Right Fit</h2>

<ul>
  <li>Your questions are repeatedly avoided or answered vaguely.</li>
  <li>Pricing and timelines keep changing without explanation.</li>
  <li>
    The maker pressures you to proceed before important details are agreed.
  </li>
  <li>Their portfolio does not reflect the type of work you want.</li>
  <li>You do not feel heard during the consultation.</li>
</ul>

        <h2>Communication and Collaboration</h2>

        <p>
          Custom making should be a thoughtful and collaborative journey, so
          don&apos;t be afraid to express your thoughts, ask questions and seek
          clarification.
        </p>

        <p>
          However, you can sense when things are not going well. Your questions
          may not be answered clearly, or there may be friction whenever you try
          to seek clarification.
        </p>

        <p>
          In this type of situation, it may be better not to go further with the
          collaboration, as it could end in a very dissatisfying transaction.
        </p>

        <h2>Final Thoughts</h2>

        <p>
          The right custom maker is not simply the person who can make the type of
          piece you want. It is also someone whose specialty, quality of work,
          process and communication style align with your expectations.
        </p>

        <p>
  A skilled custom maker may sometimes recommend a different fabric,
  construction method or design detail. This does not necessarily mean they
  are ignoring your request. The important thing is that they explain their
  reasoning and continue to involve you in the decision.
</p>

        <p>
          When you and the custom maker understand each other, communicate
          clearly and work well together, you are more likely
          to end up with a successful and satisfying outcome.
        </p>
      </>
    ),
  },

  {
    slug: 'why-tailoring-matters',
    title: 'Why Tailoring Matters',
    label: 'Weekly Edition',
    category: 'Weekly Edition',
    publishedAt: '2026-07-10',
    summary:
      'Discover why tailoring transforms the way clothing fits, feels, and lasts.',
    welcomeGift: false,
    content: (
      <>
        <p>This article is currently in development.</p>
      </>
    )
  }
]
